import { Button } from "@/components/ui/button";
import { Sun, Moon, Globe } from "lucide-react";
import cswLogo from "@/assets/csw-logo-new.png";
import { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// SSR-safe theme hook
const useThemeSafe = () => {
  const [theme, setThemeState] = useState<string>("dark");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Check for stored theme or system preference
    const stored = localStorage.getItem("theme");
    if (stored) {
      setThemeState(stored);
      document.documentElement.classList.toggle("dark", stored === "dark");
    } else {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      setThemeState(prefersDark ? "dark" : "light");
      document.documentElement.classList.toggle("dark", prefersDark);
    }
  }, []);

  const setTheme = (newTheme: string) => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
  };

  return { theme, setTheme, mounted };
};

export const Header = () => {
  const { theme, setTheme, mounted } = useThemeSafe();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-xl" role="banner">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <a href="/" className="flex items-center hover:opacity-80 transition-opacity" aria-label="CSW Home">
            <img 
              src={cswLogo} 
              alt="CSW - Chill Study Work Logo" 
              className="h-10 w-auto"
            />
          </a>
          
          {/* Right Section: Theme Toggle, Language Switcher, CTA */}
          <nav className="flex items-center gap-4" aria-label="Header navigation">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="w-10 h-10 rounded-lg hover:bg-accent"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" aria-hidden="true" />
              <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" aria-hidden="true" />
              <span className="sr-only">Toggle theme</span>
            </Button>

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="w-10 h-10 rounded-lg hover:bg-accent group" aria-label="Change language">
                  <Globe className="h-5 w-5 group-hover:text-primary transition-colors" aria-hidden="true" />
                  <span className="sr-only">Change language</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-2 bg-card border-border shadow-xl">
                <div className="px-2 py-2 mb-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Select Language</p>
                </div>
                <div className="grid grid-cols-1 gap-1">
                  <DropdownMenuItem asChild className="rounded-lg px-3 py-2.5 focus:bg-primary/10 cursor-pointer">
                    <a href="/" className="flex items-center gap-3">
                      <span className="text-lg">🇺🇸</span>
                      <span className="font-medium">English</span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="rounded-lg px-3 py-2.5 focus:bg-primary/10 cursor-pointer">
                    <a href="/fr/" className="flex items-center gap-3">
                      <span className="text-lg">🇫🇷</span>
                      <span className="font-medium">Français</span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="rounded-lg px-3 py-2.5 focus:bg-primary/10 cursor-pointer">
                    <a href="/jp/" className="flex items-center gap-3">
                      <span className="text-lg">🇯🇵</span>
                      <span className="font-medium">日本語</span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="rounded-lg px-3 py-2.5 focus:bg-primary/10 cursor-pointer">
                    <a href="/vn/" className="flex items-center gap-3">
                      <span className="text-lg">🇻🇳</span>
                      <span className="font-medium">Tiếng Việt</span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="rounded-lg px-3 py-2.5 focus:bg-primary/10 cursor-pointer">
                    <a href="/cn/" className="flex items-center gap-3">
                      <span className="text-lg">🇨🇳</span>
                      <span className="font-medium">简体中文</span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="rounded-lg px-3 py-2.5 focus:bg-primary/10 cursor-pointer">
                    <a href="/tw/" className="flex items-center gap-3">
                      <span className="text-lg">🇹🇼</span>
                      <span className="font-medium">繁體中文</span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="rounded-lg px-3 py-2.5 focus:bg-primary/10 cursor-pointer">
                    <a href="/kr/" className="flex items-center gap-3">
                      <span className="text-lg">🇰🇷</span>
                      <span className="font-medium">한국어</span>
                    </a>
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CTA Button */}
            <Button 
              asChild
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 transition-all hover:scale-105"
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Join Now For Free
              </a>
            </Button>
          </nav>
        </div>
      </div>
    </header>
  );
};
